import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { workspacePlanLabel } from '../../config/plans';
import {
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    Icon,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import {
    FiActivity,
    FiBarChart2,
    FiCalendar,
    FiCheckCircle,
    FiDatabase,
    FiFileText,
    FiFlag,
    FiKey,
    FiLayers,
    FiPhone,
    FiShield,
    FiTarget,
    FiTrendingUp,
    FiUploadCloud,
    FiUsers,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import SyntheticPreviewBanner from '../../compnents/SyntheticPreviewBanner';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import {
    ActionList,
    ExportButton,
    KpiTile,
    ProgressMetric,
    WorkspaceCard,
    WorkspaceHero,
} from '../../compnents/B2BWorkspacePrimitives';
import {
    buildSyntheticWhoToCall,
    demoCustomers,
    getCustomerDisplayName,
} from '../../content/demoAnalytics';
import BookImpactPanel from '../../compnents/BookImpactPanel';
import useTodayWorkspaceData, {
    FIRM_DATASET_TYPES,
    formatPct,
    severityTone,
} from '../../hooks/useTodayWorkspaceData';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const safeCompanyName = (user) => (
    user?.businessName ||
    user?.business_name ||
    user?.companyName ||
    user?.name ||
    'your workspace'
);

const planLabel = (subscription, user = null) => {
    const label = workspacePlanLabel(user || { subscription });
    return label || 'Demo';
};

const smallBadgeTone = {
    high: { bg: 'red.50', color: 'red.700' },
    medium: { bg: 'orange.50', color: 'orange.700' },
    low: { bg: 'brand.tealSoft', color: 'brand.tealDark' },
    ok: { bg: 'green.50', color: 'green.700' },
    info: { bg: 'blue.50', color: 'blue.700' },
};

const StatusBadge = ({ children, tone = 'info' }) => (
    <Badge bg={smallBadgeTone[tone]?.bg || 'brand.tealSoft'} color={smallBadgeTone[tone]?.color || 'brand.tealDark'}>
        {children}
    </Badge>
);

// Urgency comes from the engine and is relative to the adviser's book
// (policy priority-2026.10); the priority score alone is not a deadline.
const formatCount = (value) => Number(value || 0).toLocaleString('en-US');
const urgencyOf = (item) => String(item.urgency_level || '').toUpperCase();
const DUE_BY_URGENCY = { HIGH: 'Today', MEDIUM: 'This week' };

const severityRank = { critical: 0, high: 1, medium: 2, low: 3 };
const bySeverity = (a, b) => (severityRank[a.severity] ?? 9) - (severityRank[b.severity] ?? 9);

// Compact human-readable metric for a product-data signal row.
const signalMetric = (signal) => {
    const evidence = signal.evidence || {};
    if (Number.isFinite(Number(evidence.drift_pct))) {
        return `${Number(evidence.drift_pct) > 0 ? '+' : ''}${evidence.drift_pct}pp vs model band`;
    }
    if (evidence.product_risk_level !== undefined) {
        return `product risk ${evidence.product_risk_level} vs tolerance ${evidence.customer_risk_profile}`;
    }
    if (evidence.product_knowledge_min !== undefined) {
        return `knowledge ${evidence.customer_knowledge_score} vs required ${evidence.product_knowledge_min}`;
    }
    if (evidence.completeness_pct !== undefined) {
        return `${evidence.completeness_pct ?? 0}% complete · ${evidence.review_status || 'pending'}`;
    }
    if (evidence.transaction_id) {
        return `trade ${evidence.transaction_id}${evidence.trade_date ? ` · ${evidence.trade_date}` : ''}`;
    }
    if (evidence.holding_rows !== undefined) {
        return `${evidence.holding_rows} holding rows affected`;
    }
    return '—';
};

const signalSubject = (signal) => {
    const evidence = signal.evidence || {};
    const parts = [evidence.customer_id, evidence.product_name || evidence.product_id].filter(Boolean);
    return parts.length ? parts.join(' · ') : signal.title;
};

const signalToRow = (signal) => ({
    name: signalSubject(signal),
    metric: signalMetric(signal),
    status: signal.severity,
    action: signal.recommended_action,
    tone: severityTone(signal.severity),
});

const LoadingPane = ({ label = 'Loading live tenant data…' }) => (
    <Flex justify="center" align="center" py={16}>
        <HStack spacing={3}>
            <Spinner color="brand.teal" />
            <Text color="brand.subtle" fontSize="sm">{label}</Text>
        </HStack>
    </Flex>
);

// Honest activation panel shown instead of fabricated numbers when the firm
// datasets behind a view are not loaded yet.
const NextUploadsCard = ({
    title = 'Activate this view with firm data',
    description = 'These values are computed from tenant datasets. Nothing is shown until the matching files are uploaded.',
    nextBestUploads = [],
    isAdmin,
    navigate,
}) => (
    <WorkspaceCard>
        <HStack spacing={3} mb={2}>
            <Icon as={FiUploadCloud} color="brand.tealDark" boxSize={5} />
            <Heading size="sm" color="brand.ink">{title}</Heading>
        </HStack>
        <Text fontSize="13px" color="brand.muted" lineHeight="1.6">{description}</Text>
        {nextBestUploads.length ? (
            <VStack align="stretch" spacing={2} mt={4}>
                {nextBestUploads.slice(0, 4).map((message) => (
                    <HStack key={message} align="flex-start" spacing={2}>
                        <Icon as={FiDatabase} color="brand.subtle" mt="3px" boxSize={3.5} />
                        <Text fontSize="12.5px" color="brand.muted">{message}</Text>
                    </HStack>
                ))}
            </VStack>
        ) : null}
        <Box mt={5}>
            {isAdmin ? (
                <Button size="sm" bg="brand.teal" color="brand.navy" leftIcon={<FiUploadCloud />} onClick={() => navigate('/datasets')}>
                    Open datasets
                </Button>
            ) : (
                <Text fontSize="12px" color="brand.subtle">
                    Ask your firm admin to upload these datasets from the Datasets workspace.
                </Text>
            )}
        </Box>
    </WorkspaceCard>
);

export function AdminTodayView() {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const company = safeCompanyName(user);
    const uploads = user?.noOfFileUploads || 0;
    const plan = planLabel(user?.subscription, user);
    const { counts, coverage, sufficiency } = useTodayWorkspaceData({ withSignals: false });
    const pct = (value) => (Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0);

    return (
        <VStack spacing={5} align="stretch">
            {user?.subscription === 'business' ? <BookImpactPanel /> : null}
            <WorkspaceHero
                eyebrow="Today · Admin workspace"
                title={`Welcome back to ${company}.`}
                accent="The workspace is ready for operating reviews."
                description={`Plan ${plan}. Customer scoring, RM coverage, exports, and governance controls are grouped here so the admin can move from setup to action without hunting through tabs.`}
                primaryAction={{
                    label: 'Open customer base',
                    icon: FiUsers,
                    onClick: () => navigate('/dashboard/admin?view=clients'),
                }}
                secondaryAction={{
                    label: uploads > 0 ? 'Review datasets' : 'Upload dataset',
                    icon: FiDatabase,
                    onClick: () => navigate('/datasets'),
                }}
                kpis={[
                    { eyebrow: 'Datasets', value: uploads || '0', sub: uploads > 0 ? 'uploaded in tenant' : 'upload required', icon: FiDatabase },
                    { eyebrow: 'Role access', value: user?.subscription === 'business' ? '5' : '2', sub: user?.subscription === 'business' ? 'Business dashboards' : 'Admin + RM', icon: FiShield },
                    { eyebrow: 'Exports', value: 'PDF / CSV', sub: 'reports area', icon: FiFileText },
                    { eyebrow: 'Dev portal', value: user?.subscription === 'business' ? 'On' : 'Off', sub: 'Business only', icon: FiKey },
                ]}
            />

            <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={4}>
                <KpiTile eyebrow="Customer base" value="Live" sub="open score and insight views" icon={FiUsers} tone="teal" />
                <KpiTile eyebrow="RM workflow" value="Ready" sub="who-to-call and own-book queues" icon={FiPhone} />
                <KpiTile eyebrow="Compliance" value={user?.subscription === 'business' ? 'Unlocked' : 'Business'} sub="audit and evidence console" icon={FiShield} />
                <KpiTile eyebrow="White-label API" value="Custom" sub="negotiated scope only" icon={FiKey} />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5} alignItems="stretch">
                <ActionList
                    title="Admin actions"
                    subtitle="Dataset, scoring, team and governance work in one place."
                    actions={[
                        {
                            badge: 'Dataset',
                            title: uploads > 0 ? 'Review the latest uploaded customer file' : 'Upload the customer CSV/XLSX template',
                            description: uploads > 0
                                ? 'Check processing coverage, rejected rows, and that scoring completed for every customer.'
                                : 'The paid workspace needs a customer template before live dashboards can render tenant data.',
                            cta: uploads > 0 ? 'Open datasets' : 'Upload file',
                            icon: FiDatabase,
                            time: 'now',
                            onClick: () => navigate('/datasets'),
                        },
                        {
                            badge: 'Role access',
                            title: user?.subscription === 'business' ? 'Business role dashboards are available' : 'Solo and Team include the Admin and Relationship Manager workspaces',
                            description: user?.subscription === 'business'
                                ? 'Head of Advisory, Compliance, and Portfolio Adviser can be opened from the role workspace switcher.'
                                : 'Use the demo workspace to show Business roles, or upgrade the tenant before inviting those users.',
                            cta: user?.subscription === 'business' ? 'Open Head' : 'Open RM',
                            icon: FiShield,
                            onClick: () => navigate(user?.subscription === 'business' ? '/dashboard/head?view=today' : '/dashboard/rm?view=today'),
                        },
                        {
                            badge: 'Reports',
                            title: 'Prepare a board-ready export pack',
                            description: 'Generate PDF and CSV report packs for compliance and management, with PNG snapshots for quick sharing.',
                            cta: 'Open exports',
                            icon: FiFileText,
                            onClick: () => navigate('/reportsExports'),
                        },
                    ]}
                />

                <WorkspaceCard p={0}>
                    <Box px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider">
                        <Heading size="sm" color="brand.ink">Workspace health</Heading>
                        <Text mt={1} fontSize="12px" color="brand.subtle">High-level operating checks for your workspace setup.</Text>
                    </Box>
                    <VStack spacing={4} align="stretch" p={5}>
                        {/* Computed from the tenant's data sufficiency, never fixed values. */}
                        <ProgressMetric
                            label="Customers scored"
                            value={pct(coverage.scored_customer_pct)}
                            helper={sufficiency ? `${formatCount(counts.scored_customers)} of ${formatCount(counts.customers)} customers` : 'Available once data sufficiency loads'}
                        />
                        <ProgressMetric
                            label="Customers with holdings"
                            value={pct(coverage.customers_with_holdings_pct)}
                            helper={sufficiency ? `${formatCount(counts.holdings)} holdings across the book` : 'Upload holdings to measure exposure in euros'}
                        />
                        <ProgressMetric
                            label="Customers with suitability evidence"
                            value={pct(coverage.customers_with_evidence_pct)}
                            helper={sufficiency ? `${formatCount(counts.suitability_evidence)} evidence records` : 'Upload suitability evidence to audit advice'}
                        />
                        <Flex pt={2} gap={3} flexWrap="wrap">
                            <Button size="sm" onClick={() => navigate('/settings')}>Settings</Button>
                            <Button size="sm" variant="outline" onClick={() => navigate('/subscription')}>Subscription</Button>
                            <Button size="sm" variant="outline" onClick={() => navigate('/auditsCompliance')}>Audits</Button>
                        </Flex>
                    </VStack>
                </WorkspaceCard>
            </SimpleGrid>
        </VStack>
    );
}

export function RMTodayView() {
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);
    const [queue, setQueue] = useState(() => buildSyntheticWhoToCall(demoCustomers).slice(0, 6));

    useEffect(() => {
        let cancelled = false;

        const loadQueue = async () => {
            if (!token) return;

            try {
                const response = await axios.get(`${API_BASE_URL}/content/rm-who-to-call`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const items = Array.isArray(response.data) ? response.data : [];

                if (!cancelled) {
                    setQueue(items.length > 0 ? buildSyntheticWhoToCall(items).slice(0, 6) : buildSyntheticWhoToCall(demoCustomers).slice(0, 6));
                }
            } catch (error) {
                if (!cancelled) setQueue(buildSyntheticWhoToCall(demoCustomers).slice(0, 6));
            }
        };

        loadQueue();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const isSyntheticQueue = queue.length > 0 && queue.every((item) => item.is_synthetic);
    const highUrgency = queue.filter((item) => urgencyOf(item) === 'HIGH').length;
    const avgPriority = queue.length
        ? (queue.reduce((sum, item) => sum + Number(item.priority_score || 0), 0) / queue.length).toFixed(1)
        : '0.0';
    // Share of behavioural indicators backed by observed data, from the engine.
    const coverageItems = queue.filter((item) => Number.isFinite(Number(item.evidence_coverage)));
    const avgCoverage = coverageItems.length
        ? Math.round(coverageItems.reduce((sum, item) => sum + Number(item.evidence_coverage) * 100, 0) / coverageItems.length)
        : null;

    return (
        <VStack spacing={5} align="stretch">
            {isSyntheticQueue ? (
                <SyntheticPreviewBanner message="No scored customers are available for this account yet, so the queue below is a synthetic sample. Upload a customer dataset to rank your own book." />
            ) : null}
            <WorkspaceHero
                eyebrow="Today · Relationship Manager"
                title="Your next conversations are ranked."
                accent="Start from who-to-call, then drill into client evidence."
                description="The RM workspace is action-first: priority, reason, recommended framing, and follow-up paths are visible before the deeper analytics tabs."
                primaryAction={{ label: 'Open who-to-call', icon: FiPhone, onClick: () => navigate('/dashboard/rm?view=who-to-call') }}
                secondaryAction={{ label: 'Open own book', icon: FiUsers, onClick: () => navigate('/dashboard/rm?view=own-book') }}
                kpis={[
                    { eyebrow: 'Queue', value: queue.length, sub: 'ranked clients', icon: FiFlag },
                    { eyebrow: 'High urgency', value: highUrgency, sub: 'due today', icon: FiActivity },
                    { eyebrow: 'Avg priority', value: `${avgPriority}/10`, sub: isSyntheticQueue ? 'synthetic sample' : 'scored queue', icon: FiTrendingUp },
                    { eyebrow: 'Evidence', value: avgCoverage === null ? '—' : `${avgCoverage}%`, sub: 'indicators observed', icon: FiTarget },
                ]}
            />

            <WorkspaceCard p={0}>
                <Flex justify="space-between" align="center" px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider" gap={4}>
                    <Box>
                        <Heading size="sm">Who-to-call queue</Heading>
                        <Text mt={1} fontSize="12px" color="brand.subtle">Client-level CTA, urgency, top bias, and rationale in one row.</Text>
                    </Box>
                    <ExportButton onClick={() => navigate('/reportsExports')} />
                </Flex>
                <Box overflowX="auto">
                    <Table size="sm">
                        <Thead bg="brand.cream">
                            <Tr>
                                <Th>Client</Th>
                                <Th>Urgency</Th>
                                <Th>Top bias</Th>
                                <Th>Why now</Th>
                                <Th>Due</Th>
                                <Th />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {queue.map((item, index) => {
                                const customerId = item.customer_id || item.id;
                                const priority = Number(item.priority_score || item.urgency || 0);
                                const urgency = urgencyOf(item);
                                const urgencyTone = urgency === 'HIGH' ? 'high' : urgency === 'MEDIUM' ? 'medium' : 'low';
                                const topBias = Array.isArray(item.top_biases) && item.top_biases.length > 0 ? item.top_biases[0] : null;
                                return (
                                <Tr key={customerId} bg={index === 0 ? 'brand.tealSoft' : 'white'}>
                                    <Td>
                                        <Text fontWeight="800">{getCustomerDisplayName(item)}</Text>
                                        <Text fontSize="11px" color="brand.subtle" fontFamily="mono">{customerId}</Text>
                                    </Td>
                                    <Td>
                                        <HStack>
                                            <Text fontWeight="900" color={urgency === 'HIGH' ? 'red.600' : 'orange.600'}>{priority}</Text>
                                            <StatusBadge tone={urgencyTone}>{item.urgency_level || 'Low'}</StatusBadge>
                                        </HStack>
                                    </Td>
                                    <Td fontWeight="800">
                                        {topBias ? `${topBias.bias_name}${Number.isFinite(Number(topBias.score)) ? ` · ${Math.round(Number(topBias.score))}` : ''}` : '—'}
                                    </Td>
                                    <Td color="brand.muted">{item.recommended_action || item.why}</Td>
                                    <Td><StatusBadge tone={urgencyTone}>{DUE_BY_URGENCY[urgency] || 'Next review'}</StatusBadge></Td>
                                    <Td textAlign="right">
                                        <Button size="sm" bg="brand.teal" color="brand.navy" onClick={() => navigate(`/dashboard/rm?view=who-to-call&search=${encodeURIComponent(customerId)}`)}>
                                            Open brief
                                        </Button>
                                    </Td>
                                </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            </WorkspaceCard>
        </VStack>
    );
}

export function HeadTodayView() {
    const navigate = useNavigate();
    const role = useContextRole();
    const data = useTodayWorkspaceData({ withActions: true });
    const { loading, counts, coverage, typeCounts, totalSignals, signalsAvailable, actions, nextBestUploads } = data;

    if (loading) return <LoadingPane />;

    const actionList = actions || [];
    const actionsCount = actions ? actionList.length : (counts.rm_actions ?? 0);
    const completedActions = actionList.filter((action) => action.status === 'done').length;
    const deferredActions = actionList.filter((action) => action.status === 'snoozed').length;
    const driftCount = typeCounts.drift;
    const evidencePending = typeCounts.evidence + typeCounts.transaction_gap;

    const leadershipActions = [];
    if (driftCount > 0) {
        leadershipActions.push({
            badge: 'Drift',
            title: `${formatCount(driftCount)} holding${driftCount === 1 ? ' is' : 's are'} outside model bands`,
            description: 'Review the live allocation-drift queue and push rebalance checks to the portfolio owners.',
            cta: 'Open risk trend',
            icon: FiTrendingUp,
            toneBg: 'orange.50',
            toneColor: 'orange.700',
            onClick: () => navigate('/dashboard/head?view=risk'),
        });
    }
    if (evidencePending > 0) {
        leadershipActions.push({
            badge: 'Evidence',
            title: `${formatCount(evidencePending)} suitability evidence item${evidencePending === 1 ? '' : 's'} pending`,
            description: 'Evidence gaps block defensible recommendations. Confirm owners before the next review cycle.',
            cta: 'Open risk trend',
            icon: FiShield,
            onClick: () => navigate('/dashboard/head?view=risk'),
        });
    }
    leadershipActions.push(actionsCount === 0
        ? {
            badge: 'Adoption',
            title: 'No RM follow-up actions logged yet',
            description: 'Actions logged from the who-to-call queue power the adoption and conversion readouts for this role.',
            cta: 'Open adoption',
            icon: FiUsers,
            onClick: () => navigate('/dashboard/head?view=adoption'),
        }
        : {
            badge: 'Adoption',
            title: `${actionsCount} follow-up action${actionsCount === 1 ? '' : 's'} logged by the team`,
            description: actions
                ? `${completedActions} completed · ${deferredActions} deferred. Compare against surfaced priorities in the conversion view.`
                : 'Open the conversion view for the completion breakdown.',
            cta: 'Open conversion',
            icon: FiTarget,
            onClick: () => navigate('/dashboard/head?view=conversion'),
        });

    return (
        <VStack spacing={5} align="stretch">
            <WorkspaceHero
                eyebrow="Today · Head of Advisory"
                title="The advisory floor in one screen."
                accent="Live coverage, team activity, and open risk signals."
                description="Every number below is computed from this tenant: scored coverage from the customer base, follow-up activity from RM actions, and risk signals from the firm-ready datasets."
                primaryAction={{ label: 'Open bias benchmark', icon: FiBarChart2, onClick: () => navigate('/dashboard/head?view=bias') }}
                secondaryAction={{ label: 'Export weekly brief', icon: FiFileText, onClick: () => navigate('/reportsExports') }}
                kpis={[
                    { eyebrow: 'Scored base', value: formatPct(coverage.scored_customer_pct), sub: 'customers scored', icon: FiShield },
                    { eyebrow: 'Client base', value: counts.customers ?? '—', sub: 'in tenant', icon: FiUsers },
                    { eyebrow: 'RM actions', value: actionsCount, sub: 'logged follow-ups', icon: FiActivity },
                    { eyebrow: 'Open signals', value: signalsAvailable ? (totalSignals ?? 0) : '—', sub: signalsAvailable ? 'live data signals' : 'requires firm datasets', icon: FiFlag },
                ]}
            />

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
                <ActionList
                    title="Leadership signals"
                    subtitle="Computed from tenant data — each row links to the matching review queue."
                    actions={leadershipActions}
                />

                <WorkspaceCard p={0}>
                    <Box px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider">
                        <Heading size="sm">Advisory health</Heading>
                        <Text mt={1} fontSize="12px" color="brand.subtle">Coverage of the datasets that make advisory decisions defensible.</Text>
                    </Box>
                    <VStack spacing={4} align="stretch" p={5}>
                        <ProgressMetric label="Customer scoring coverage" value={Math.round(Number(coverage.scored_customer_pct) || 0)} helper="Share of the customer base with behavioral scores" />
                        <ProgressMetric label="Suitability evidence coverage" value={Math.round(Number(coverage.customers_with_evidence_pct) || 0)} helper="Customers with linked suitability evidence" color={Number(coverage.customers_with_evidence_pct) >= 80 ? 'brand.teal' : 'orange.400'} />
                        <ProgressMetric label="Holdings coverage" value={Math.round(Number(coverage.customers_with_holdings_pct) || 0)} helper="Customers with position data for drift and fit analytics" color={Number(coverage.customers_with_holdings_pct) >= 80 ? 'brand.teal' : 'orange.400'} />
                    </VStack>
                </WorkspaceCard>
            </SimpleGrid>

            {!signalsAvailable || (counts.holdings || 0) === 0 ? (
                <NextUploadsCard
                    title="Unlock drift and mismatch signals for this view"
                    nextBestUploads={nextBestUploads}
                    isAdmin={role === 'admin'}
                    navigate={navigate}
                />
            ) : null}
        </VStack>
    );
}

// Small helper so views can branch CTAs on the signed-in role without
// re-importing the context everywhere.
function useContextRole() {
    const { user } = useContext(AuthContext);
    return user?.role;
}

export function ComplianceTodayView() {
    const navigate = useNavigate();
    const role = useContextRole();
    const data = useTodayWorkspaceData();
    const { loading, counts, coverage, byType, typeCounts, signalsAvailable, nextBestUploads } = data;

    if (loading) return <LoadingPane />;

    const evidencePending = typeCounts.evidence;
    const transactionGaps = typeCounts.transaction_gap;
    const riskMismatches = typeCounts.risk_mismatch + typeCounts.knowledge_mismatch;
    const complianceSignals = [
        ...byType.risk_mismatch,
        ...byType.knowledge_mismatch,
        ...byType.transaction_gap,
        ...byType.evidence,
    ].sort(bySeverity);
    const hasComplianceData = (counts.suitability_evidence || 0) > 0 || (counts.transactions || 0) > 0 || riskMismatches > 0;

    const scoredPct = Number(coverage.scored_customer_pct) || 0;
    const dailyChecks = [
        ['Customer scoring coverage', `${Math.round(scoredPct)}%`, scoredPct >= 90 ? 'ok' : scoredPct >= 50 ? 'medium' : 'high'],
        ['Suitability evidence dataset', (counts.suitability_evidence || 0) > 0 ? `${formatCount(counts.suitability_evidence)} records` : 'MISSING', (counts.suitability_evidence || 0) > 0 ? 'ok' : 'high'],
        ['Transactions dataset', (counts.transactions || 0) > 0 ? `${formatCount(counts.transactions)} records` : 'MISSING', (counts.transactions || 0) > 0 ? 'ok' : 'medium'],
        ['Product catalog', (counts.products || 0) > 0 ? `${counts.products} products` : 'MISSING', (counts.products || 0) > 0 ? 'ok' : 'medium'],
        ['Advised trades without evidence', signalsAvailable ? (transactionGaps === 0 ? 'NONE OPEN' : `${formatCount(transactionGaps)} OPEN`) : 'NEEDS DATA', signalsAvailable ? (transactionGaps === 0 ? 'ok' : 'high') : 'medium'],
    ];

    const findings = complianceSignals.slice(0, 3).map((signal) => ({
        badge: signal.severity.toUpperCase(),
        title: signal.title,
        description: `${signalSubject(signal)} · ${signal.recommended_action}`,
        cta: 'Open queue',
        icon: FiShield,
        toneBg: severityTone(signal.severity) === 'high' ? 'red.50' : 'orange.50',
        toneColor: severityTone(signal.severity) === 'high' ? 'red.700' : 'orange.700',
        onClick: () => navigate('/dashboard/compliance?view=sla'),
    }));

    return (
        <VStack spacing={5} align="stretch">
            <WorkspaceHero
                eyebrow="Today · Compliance"
                title="Evidence, review queues, and audit exports."
                accent="Computed from this tenant's datasets."
                description="The compliance workspace surfaces missing evidence, unlinked advised trades, and risk mismatches calculated live from the firm-ready datasets — no projected or sample values."
                primaryAction={{ label: 'Open review queue', icon: FiShield, onClick: () => navigate('/dashboard/compliance?view=sla') }}
                secondaryAction={{ label: 'Generate report pack', icon: FiFileText, onClick: () => navigate('/reportsExports') }}
                kpis={[
                    { eyebrow: 'Evidence queue', value: signalsAvailable ? evidencePending + transactionGaps : '—', sub: signalsAvailable ? 'items needing review' : 'requires datasets', icon: FiFileText },
                    { eyebrow: 'Evidence coverage', value: formatPct(coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                    { eyebrow: 'Risk mismatches', value: signalsAvailable ? riskMismatches : '—', sub: 'product vs tolerance', icon: FiFlag },
                    { eyebrow: 'Scored base', value: formatPct(coverage.scored_customer_pct), sub: 'customers scored', icon: FiUsers },
                ]}
            />

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
                {findings.length > 0 ? (
                    <ActionList
                        title="Open compliance findings"
                        subtitle="Highest-severity live signals first. Open the queue to review and assign."
                        actions={findings}
                    />
                ) : (
                    <NextUploadsCard
                        title={hasComplianceData ? 'No open compliance findings' : 'Activate compliance queues with firm data'}
                        description={hasComplianceData
                            ? 'No evidence gaps, unlinked advised trades, or risk mismatches are currently detected in the loaded datasets.'
                            : 'Compliance queues are computed from suitability evidence, transactions, and the product catalog. Nothing is simulated.'}
                        nextBestUploads={hasComplianceData ? [] : nextBestUploads}
                        isAdmin={role === 'admin'}
                        navigate={navigate}
                    />
                )}

                <WorkspaceCard p={0}>
                    <Box px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider">
                        <Heading size="sm">Daily checks</Heading>
                        <Text mt={1} fontSize="12px" color="brand.subtle">Dataset and control coverage computed from the live tenant.</Text>
                    </Box>
                    <VStack spacing={3} align="stretch" p={5}>
                        {dailyChecks.map(([label, state, tone]) => (
                            <Flex key={label} align="center" gap={3} bg={tone === 'ok' ? 'green.50' : tone === 'high' ? 'red.50' : 'orange.50'} borderRadius="10px" px={3} py={2}>
                                <Icon as={tone === 'ok' ? FiCheckCircle : FiFlag} color={tone === 'ok' ? 'green.600' : tone === 'high' ? 'red.600' : 'orange.600'} />
                                <Text flex="1" fontSize="13px" fontWeight="700">{label}</Text>
                                <StatusBadge tone={tone}>{state}</StatusBadge>
                            </Flex>
                        ))}
                    </VStack>
                </WorkspaceCard>
            </SimpleGrid>
        </VStack>
    );
}

export function PortfolioTodayView() {
    const navigate = useNavigate();
    const role = useContextRole();
    const data = useTodayWorkspaceData();
    const { loading, counts, coverage, byType, typeCounts, worstDriftPp, signalsAvailable, nextBestUploads } = data;

    if (loading) return <LoadingPane />;

    const hasPortfolioData = (counts.products || 0) > 0 && (counts.holdings || 0) > 0;
    const driftSignals = byType.drift;
    const mismatchSignals = [...byType.risk_mismatch, ...byType.knowledge_mismatch].sort(bySeverity);
    const structuralGaps = [...byType.missing_catalog, ...byType.model_gap];
    const reviewRows = [...mismatchSignals, ...driftSignals, ...structuralGaps].sort(bySeverity).slice(0, 6);

    const mismatchCount = typeCounts.risk_mismatch + typeCounts.knowledge_mismatch;
    const topSignal = reviewRows[0] || null;

    return (
        <VStack spacing={5} align="stretch">
            <WorkspaceHero
                eyebrow="Today · Portfolio Adviser"
                title="Portfolio fit, drift, and suitability evidence."
                accent="Signals computed from holdings, catalog, and model targets."
                description="Drift and mismatch below come from comparing live holdings against the tenant product catalog and model portfolio bands. If a number is missing, the dataset behind it has not been uploaded."
                primaryAction={{ label: 'Open mismatch review', icon: FiLayers, onClick: () => navigate('/dashboard/portfolio?view=mismatch') }}
                secondaryAction={{ label: 'Export review pack', icon: FiFileText, onClick: () => navigate('/reportsExports') }}
                kpis={[
                    { eyebrow: 'Mismatch cases', value: signalsAvailable ? mismatchCount : '—', sub: signalsAvailable ? 'risk/knowledge vs holdings' : 'requires datasets', icon: FiFlag },
                    { eyebrow: 'Worst drift', value: typeCounts.drift && worstDriftPp !== null ? `${worstDriftPp.toFixed(1)}pp` : (hasPortfolioData && signalsAvailable ? 'In band' : '—'), sub: typeCounts.drift ? `${formatCount(typeCounts.drift)} holdings outside model band` : 'vs model targets', icon: FiActivity },
                    { eyebrow: 'Evidence', value: formatPct(coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                    { eyebrow: 'Holdings coverage', value: formatPct(coverage.customers_with_holdings_pct), sub: 'customers with positions', icon: FiTrendingUp },
                ]}
            />

            {reviewRows.length > 0 ? (
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
                    <WorkspaceCard p={0}>
                        <Flex justify="space-between" align="center" px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider" gap={4}>
                            <Box>
                                <Heading size="sm">Portfolio review queue</Heading>
                                <Text mt={1} fontSize="12px" color="brand.subtle">Live drift, mismatch, and catalog gaps ranked by severity.</Text>
                            </Box>
                            <ExportButton onClick={() => navigate('/reportsExports')} />
                        </Flex>
                        <Box overflowX="auto">
                            <Table size="sm">
                                <Thead bg="brand.cream">
                                    <Tr>
                                        <Th>Client / product</Th>
                                        <Th>Metric</Th>
                                        <Th>Severity</Th>
                                        <Th />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {reviewRows.map((signal) => (
                                        <Tr key={signal.signal_id} bg={severityTone(signal.severity) === 'high' ? 'red.50' : 'white'}>
                                            <Td>
                                                <Text fontWeight="800">{signalSubject(signal)}</Text>
                                                <Text fontSize="11px" color="brand.subtle">{signal.title}</Text>
                                            </Td>
                                            <Td color="brand.muted">{signalMetric(signal)}</Td>
                                            <Td><StatusBadge tone={severityTone(signal.severity)}>{signal.severity}</StatusBadge></Td>
                                            <Td textAlign="right">
                                                <Button size="sm" variant="outline" onClick={() => navigate(byType.drift.includes(signal) ? '/dashboard/portfolio?view=drift' : '/dashboard/portfolio?view=mismatch')}>
                                                    Inspect
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </WorkspaceCard>

                    <WorkspaceCard>
                        <Heading size="sm">{topSignal.title}</Heading>
                        <Text mt={2} color="brand.muted" fontSize="13px" lineHeight="1.6">
                            {topSignal.description}
                        </Text>
                        <VStack align="stretch" spacing={3} mt={5}>
                            <HStack justify="space-between">
                                <Text fontSize="13px" fontWeight="700" color="brand.ink">{signalSubject(topSignal)}</Text>
                                <StatusBadge tone={severityTone(topSignal.severity)}>{topSignal.severity}</StatusBadge>
                            </HStack>
                            <Text fontSize="13px" color="brand.muted">{signalMetric(topSignal)}</Text>
                            <Text fontSize="13px" color="brand.ink" fontWeight="600">{topSignal.recommended_action}</Text>
                        </VStack>
                        <Button mt={5} bg="brand.teal" color="brand.navy" onClick={() => navigate('/dashboard/portfolio?view=mismatch')}>
                            Open review queue
                        </Button>
                    </WorkspaceCard>
                </SimpleGrid>
            ) : (
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
                    <NextUploadsCard
                        title={hasPortfolioData && signalsAvailable ? 'No open portfolio signals' : 'Activate drift and mismatch analytics'}
                        description={hasPortfolioData && signalsAvailable
                            ? 'All loaded holdings are inside their model bands and no product-fit mismatches are detected.'
                            : 'Drift and mismatch are computed from the product catalog, customer holdings, and model portfolio targets. Nothing is simulated in this view.'}
                        nextBestUploads={hasPortfolioData && signalsAvailable ? [] : nextBestUploads}
                        isAdmin={role === 'admin'}
                        navigate={navigate}
                    />
                    <WorkspaceCard p={0}>
                        <Box px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider">
                            <Heading size="sm">Data coverage</Heading>
                            <Text mt={1} fontSize="12px" color="brand.subtle">What the portfolio analytics can currently see.</Text>
                        </Box>
                        <VStack spacing={4} align="stretch" p={5}>
                            <ProgressMetric label="Holdings coverage" value={Math.round(Number(coverage.customers_with_holdings_pct) || 0)} helper="Customers with position data" color={Number(coverage.customers_with_holdings_pct) >= 80 ? 'brand.teal' : 'orange.400'} />
                            <ProgressMetric label="Catalog match" value={Math.round(Number(coverage.holding_product_catalog_match_pct) || 0)} helper="Holdings matched to tenant product catalog" color={Number(coverage.holding_product_catalog_match_pct) >= 80 ? 'brand.teal' : 'orange.400'} />
                            <ProgressMetric label="Evidence coverage" value={Math.round(Number(coverage.customers_with_evidence_pct) || 0)} helper="Customers with suitability evidence" color={Number(coverage.customers_with_evidence_pct) >= 80 ? 'brand.teal' : 'orange.400'} />
                        </VStack>
                    </WorkspaceCard>
                </SimpleGrid>
            )}
        </VStack>
    );
}

// Builders turn the live tenant context into the hero + queue layout used by
// the role insight detail views. Rows are always real signals/actions; when
// the backing dataset is missing the view renders an activation panel instead.
const insightConfigBuilders = {
    head: {
        adoption: (ctx) => {
            const actionList = ctx.actions || [];
            const total = ctx.actions ? actionList.length : (ctx.counts.rm_actions ?? 0);
            const byTypeCount = (type) => actionList.filter((action) => action.action_type === type);
            const completed = actionList.filter((action) => action.status === 'done').length;
            const rows = ctx.actions
                ? [
                    ['call', 'Calls logged'],
                    ['meeting', 'Meetings booked'],
                    ['note', 'Notes recorded'],
                ].map(([type, label]) => {
                    const items = byTypeCount(type);
                    const done = items.filter((action) => action.status === 'done').length;
                    return {
                        name: label,
                        metric: `${items.length} total · ${done} completed`,
                        status: items.length > 0 ? 'Active' : 'None yet',
                        action: items.length > 0 ? 'Review in RM workflow views' : 'Logged from the who-to-call queue',
                        tone: items.length > 0 ? 'ok' : 'medium',
                    };
                }).filter((row) => total > 0)
                : (total > 0 ? [{
                    name: 'Team follow-up actions',
                    metric: `${total} logged`,
                    status: 'Active',
                    action: 'Detailed per-action breakdown is visible to firm admins.',
                    tone: 'ok',
                }] : []);

            return {
                eyebrow: 'Head of Advisory · Adoption',
                title: 'Adoption tracks how the team logs follow-up work.',
                accent: 'Computed from RM actions in this tenant.',
                description: 'Calls, meetings, and notes logged from the who-to-call queue appear here, so leadership can see whether surfaced priorities turn into tracked work.',
                primary: ['Open full dashboard', '/dashboard/head?view=dashboard'],
                secondary: ['Export leadership pack', '/reportsExports'],
                kpis: [
                    { eyebrow: 'Actions logged', value: total, sub: 'all time', icon: FiActivity },
                    { eyebrow: 'Completed', value: ctx.actions ? completed : '—', sub: 'marked done', icon: FiCheckCircle },
                    { eyebrow: 'Scored base', value: formatPct(ctx.coverage.scored_customer_pct), sub: 'customers scored', icon: FiShield },
                    { eyebrow: 'Client base', value: ctx.counts.customers ?? '—', sub: 'in tenant', icon: FiUsers },
                ],
                rows,
                empty: {
                    title: 'No RM actions logged yet',
                    description: 'Adoption activates as soon as RMs log calls, meetings, or notes from the who-to-call queue. No sample activity is shown.',
                    hint: 'Open the RM workspace and track a follow-up to see it appear here.',
                },
            };
        },
        conversion: (ctx) => {
            const actionList = ctx.actions || [];
            const total = ctx.actions ? actionList.length : (ctx.counts.rm_actions ?? 0);
            const completed = actionList.filter((action) => action.status === 'done').length;
            const deferred = actionList.filter((action) => action.status === 'snoozed').length;
            const open = actionList.filter((action) => (action.status || 'open') === 'open').length;
            const conversionPct = actionList.length ? Math.round((completed / actionList.length) * 100) : null;

            return {
                eyebrow: 'Head of Advisory · Conversion',
                title: 'Conversion follows logged actions through completion.',
                accent: 'Computed from RM action statuses.',
                description: 'Surfaced priorities only matter if they convert into completed follow-ups. This view reads the live action statuses for the tenant.',
                primary: ['Open Platform Impact', '/dashboard/admin?view=impact'],
                secondary: ['Export PDF / CSV', '/reportsExports'],
                kpis: [
                    { eyebrow: 'Logged', value: total, sub: 'follow-up actions', icon: FiFlag },
                    { eyebrow: 'Completed', value: ctx.actions ? completed : '—', sub: 'actions done', icon: FiCheckCircle },
                    { eyebrow: 'Deferred', value: ctx.actions ? deferred : '—', sub: 'snoozed', icon: FiCalendar },
                    { eyebrow: 'Conversion', value: conversionPct === null ? '—' : `${conversionPct}%`, sub: 'completed share', icon: FiTrendingUp },
                ],
                rows: ctx.actions && actionList.length > 0 ? [
                    { name: 'Completed', metric: `${completed} actions`, status: `${conversionPct}%`, action: 'Scale what converts: review completed follow-ups by segment.', tone: 'ok' },
                    { name: 'Open', metric: `${open} actions`, status: 'In progress', action: 'Check aging open actions against the who-to-call queue.', tone: open > 0 ? 'medium' : 'ok' },
                    { name: 'Deferred', metric: `${deferred} actions`, status: 'Snoozed', action: 'Review deferral reasons before the next coaching session.', tone: deferred > 0 ? 'medium' : 'ok' },
                ] : [],
                empty: {
                    title: 'No conversion data yet',
                    description: 'Conversion is measured from logged RM actions moving to completed. Nothing is simulated here.',
                    hint: 'Actions logged from the who-to-call queue feed this view automatically.',
                },
            };
        },
        risk: (ctx) => ({
            eyebrow: 'Head of Advisory · Risk signals',
            title: 'Open risk signals across the book.',
            accent: 'Live drift, mismatch, and evidence gaps.',
            description: 'These signals are computed from the firm-ready datasets: holdings vs model bands, product risk vs customer tolerance, and evidence completeness.',
            primary: ['Open bias benchmark', '/dashboard/head?view=bias'],
            secondary: ['Export leadership pack', '/reportsExports'],
            kpis: [
                { eyebrow: 'Open signals', value: ctx.signalsAvailable ? (ctx.totalSignals ?? 0) : '—', sub: 'all severities', icon: FiFlag },
                { eyebrow: 'High severity', value: ctx.signalsAvailable ? ctx.highSeverityCount(Object.keys(ctx.typeCounts)) : '—', sub: 'critical + high', icon: FiShield },
                { eyebrow: 'Drift cases', value: ctx.signalsAvailable ? ctx.typeCounts.drift : '—', sub: 'outside model bands', icon: FiActivity },
                { eyebrow: 'Mismatches', value: ctx.signalsAvailable ? ctx.typeCounts.risk_mismatch + ctx.typeCounts.knowledge_mismatch : '—', sub: 'product vs client', icon: FiTarget },
            ],
            rows: ctx.signals.slice().sort(bySeverity).slice(0, 6).map(signalToRow),
            empty: {
                title: ctx.signalsAvailable ? 'No open risk signals' : 'Risk signals need firm datasets',
                description: ctx.signalsAvailable
                    ? 'No drift, mismatch, or evidence signals are currently detected in the loaded datasets.'
                    : 'Upload the product catalog, holdings, and model targets to compute live risk signals for this view.',
                hint: ctx.signalsAvailable ? 'New signals appear automatically as datasets refresh.' : undefined,
                showUploads: !ctx.signalsAvailable,
            },
        }),
    },
    portfolio: {
        mismatch: (ctx) => {
            const mismatches = [...ctx.byType.risk_mismatch, ...ctx.byType.knowledge_mismatch].sort(bySeverity);
            return {
                eyebrow: 'Portfolio Adviser · Mismatch severity',
                title: 'Product-fit mismatches ranked by severity.',
                accent: 'Live holdings vs catalog risk and knowledge thresholds.',
                description: 'Each row is a customer holding a product whose tenant catalog risk or knowledge requirement exceeds the customer profile.',
                primary: ['Open customer base', '/dashboard/portfolio?view=clients'],
                secondary: ['Open bias benchmark', '/dashboard/portfolio?view=bias'],
                kpis: [
                    { eyebrow: 'High severity', value: ctx.signalsAvailable ? ctx.highSeverityCount(['risk_mismatch', 'knowledge_mismatch']) : '—', sub: 'review first', icon: FiFlag },
                    { eyebrow: 'Knowledge gaps', value: ctx.signalsAvailable ? ctx.typeCounts.knowledge_mismatch : '—', sub: 'below product minimum', icon: FiUsers },
                    { eyebrow: 'Catalog match', value: formatPct(ctx.coverage.holding_product_catalog_match_pct), sub: 'holdings vs catalog', icon: FiLayers },
                    { eyebrow: 'Evidence', value: formatPct(ctx.coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                ],
                rows: mismatches.slice(0, 6).map(signalToRow),
                empty: {
                    title: ctx.signalsAvailable ? 'No product-fit mismatches detected' : 'Mismatch analytics need firm datasets',
                    description: ctx.signalsAvailable
                        ? 'No holding currently exceeds the customer risk tolerance or knowledge threshold in the loaded datasets.'
                        : 'Upload the product catalog and customer holdings to compute mismatch severity for this tenant.',
                    showUploads: !ctx.signalsAvailable,
                },
            };
        },
        drift: (ctx) => {
            const driftRows = [...ctx.byType.drift, ...ctx.byType.model_gap].sort(bySeverity);
            return {
                eyebrow: 'Portfolio Adviser · Allocation drift',
                title: 'Holdings outside their model portfolio bands.',
                accent: 'Computed against uploaded model targets.',
                description: 'Drift compares each live holding weight against the min/max band of its model portfolio target. Model gaps flag holdings with no target defined.',
                primary: ['Open portfolio dashboard', '/dashboard/portfolio?view=dashboard'],
                secondary: ['Export review pack', '/reportsExports'],
                kpis: [
                    { eyebrow: 'Drift cases', value: ctx.signalsAvailable ? ctx.typeCounts.drift : '—', sub: 'outside bands', icon: FiTrendingUp },
                    { eyebrow: 'Worst drift', value: ctx.typeCounts.drift && ctx.worstDriftPp !== null ? `${ctx.worstDriftPp.toFixed(1)}pp` : '—', sub: 'vs model band', icon: FiActivity },
                    { eyebrow: 'Missing targets', value: ctx.signalsAvailable ? ctx.typeCounts.model_gap : '—', sub: 'holdings without bands', icon: FiFlag },
                    { eyebrow: 'Holdings coverage', value: formatPct(ctx.coverage.customers_with_holdings_pct), sub: 'customers with positions', icon: FiUsers },
                ],
                rows: driftRows.slice(0, 6).map(signalToRow),
                empty: {
                    title: ctx.signalsAvailable ? 'All holdings are inside model bands' : 'Drift analytics need firm datasets',
                    description: ctx.signalsAvailable
                        ? 'No live holding is outside its model portfolio min/max band right now.'
                        : 'Upload customer holdings and model portfolio targets to compute allocation drift.',
                    showUploads: !ctx.signalsAvailable,
                },
            };
        },
        evidence: (ctx) => {
            const evidenceRows = [...ctx.byType.evidence, ...ctx.byType.transaction_gap].sort(bySeverity);
            return {
                eyebrow: 'Portfolio Adviser · Suitability evidence',
                title: 'Evidence gaps linked to portfolio decisions.',
                accent: 'Live completeness from the evidence dataset.',
                description: 'Incomplete or pending evidence records, plus advised trades with no linked evidence, computed from the uploaded datasets.',
                primary: ['Open customer insights', '/dashboard/portfolio?view=clients'],
                secondary: ['Export evidence CSV', '/reportsExports'],
                kpis: [
                    { eyebrow: 'Coverage', value: formatPct(ctx.coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                    { eyebrow: 'Pending items', value: ctx.signalsAvailable ? ctx.typeCounts.evidence : '—', sub: 'incomplete or in review', icon: FiFileText },
                    { eyebrow: 'Unlinked trades', value: ctx.signalsAvailable ? ctx.typeCounts.transaction_gap : '—', sub: 'advised without evidence', icon: FiFlag },
                    { eyebrow: 'Evidence records', value: ctx.counts.suitability_evidence ?? '—', sub: 'in dataset', icon: FiDatabase },
                ],
                rows: evidenceRows.slice(0, 6).map(signalToRow),
                empty: {
                    title: ctx.signalsAvailable && (ctx.counts.suitability_evidence || 0) > 0 ? 'No evidence gaps detected' : 'Evidence analytics need firm datasets',
                    description: ctx.signalsAvailable && (ctx.counts.suitability_evidence || 0) > 0
                        ? 'All evidence records in the dataset are complete and reviewed.'
                        : 'Upload suitability evidence (and transactions) to compute completeness and gaps.',
                    showUploads: !(ctx.signalsAvailable && (ctx.counts.suitability_evidence || 0) > 0),
                },
            };
        },
    },
    compliance: {
        sla: (ctx) => {
            const queue = [
                ...ctx.byType.risk_mismatch,
                ...ctx.byType.knowledge_mismatch,
                ...ctx.byType.transaction_gap,
                ...ctx.byType.evidence,
            ].sort(bySeverity);
            return {
                eyebrow: 'Compliance · Review queue',
                title: 'The review queue is ranked by severity.',
                accent: 'Live signals from the loaded datasets.',
                description: 'Risk mismatches, unlinked advised trades, and incomplete evidence, ordered so reviewers start from the highest-severity item.',
                primary: ['Open audit console', '/auditsCompliance'],
                secondary: ['Export queue CSV', '/reportsExports'],
                kpis: [
                    { eyebrow: 'Critical', value: ctx.signalsAvailable ? queue.filter((signal) => signal.severity === 'critical').length : '—', sub: 'review first', icon: FiShield },
                    { eyebrow: 'High', value: ctx.signalsAvailable ? queue.filter((signal) => signal.severity === 'high').length : '—', sub: 'in queue', icon: FiFlag },
                    { eyebrow: 'Pending evidence', value: ctx.signalsAvailable ? ctx.typeCounts.evidence : '—', sub: 'incomplete records', icon: FiFileText },
                    { eyebrow: 'Scored base', value: formatPct(ctx.coverage.scored_customer_pct), sub: 'customers scored', icon: FiCheckCircle },
                ],
                rows: queue.slice(0, 6).map(signalToRow),
                empty: {
                    title: ctx.signalsAvailable ? 'Review queue is clear' : 'The review queue needs firm datasets',
                    description: ctx.signalsAvailable
                        ? 'No open compliance signals are detected in the loaded datasets.'
                        : 'Upload suitability evidence, transactions, and the product catalog to populate the live review queue.',
                    showUploads: !ctx.signalsAvailable,
                },
            };
        },
        evidence: (ctx) => {
            const rows = ctx.byType.evidence.slice().sort(bySeverity);
            return {
                eyebrow: 'Compliance · Missing evidence',
                title: 'Missing evidence is reviewable by customer and reason.',
                accent: 'Live completeness and review status.',
                description: 'Evidence records below the completeness threshold, or still pending review, computed from the suitability evidence dataset.',
                primary: ['Open individual scores', '/dashboard/compliance?view=scores'],
                secondary: ['Export missing evidence CSV', '/reportsExports'],
                kpis: [
                    { eyebrow: 'Pending items', value: ctx.signalsAvailable ? rows.length : '—', sub: 'to complete', icon: FiFileText },
                    { eyebrow: 'Coverage', value: formatPct(ctx.coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                    { eyebrow: 'Unlinked trades', value: ctx.signalsAvailable ? ctx.typeCounts.transaction_gap : '—', sub: 'advised without evidence', icon: FiFlag },
                    { eyebrow: 'Records', value: ctx.counts.suitability_evidence ?? '—', sub: 'in dataset', icon: FiDatabase },
                ],
                rows: rows.slice(0, 6).map(signalToRow),
                empty: {
                    title: (ctx.counts.suitability_evidence || 0) > 0 && ctx.signalsAvailable ? 'No missing evidence' : 'Evidence queues need the evidence dataset',
                    description: (ctx.counts.suitability_evidence || 0) > 0 && ctx.signalsAvailable
                        ? 'Every evidence record in the dataset is complete and reviewed.'
                        : 'Upload the suitability evidence dataset to populate completeness checks and review queues.',
                    showUploads: !((ctx.counts.suitability_evidence || 0) > 0 && ctx.signalsAvailable),
                },
            };
        },
        'audit-pack': (ctx) => {
            const loadedDatasets = FIRM_DATASET_TYPES.filter(([key]) => (ctx.counts[key] || 0) > 0);
            return {
                eyebrow: 'Compliance · Audit export',
                title: 'Audit exports are period-based PDF / CSV packs.',
                accent: 'Scope reflects the datasets actually loaded.',
                description: 'The audit pack can only certify what the tenant has uploaded. This view shows which datasets are in scope before exporting.',
                primary: ['Open reports', '/reportsExports'],
                secondary: ['Open audit console', '/auditsCompliance'],
                kpis: [
                    { eyebrow: 'Datasets loaded', value: `${loadedDatasets.length}/${FIRM_DATASET_TYPES.length}`, sub: 'firm-ready scope', icon: FiDatabase },
                    { eyebrow: 'Evidence coverage', value: formatPct(ctx.coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                    { eyebrow: 'Open signals', value: ctx.signalsAvailable ? (ctx.totalSignals ?? 0) : '—', sub: 'will appear in pack', icon: FiFlag },
                    { eyebrow: 'Scored base', value: formatPct(ctx.coverage.scored_customer_pct), sub: 'customers scored', icon: FiUsers },
                ],
                rows: FIRM_DATASET_TYPES.map(([key, label]) => {
                    const loaded = (ctx.counts[key] || 0) > 0;
                    return {
                        name: label,
                        metric: loaded ? `${formatCount(ctx.counts[key])} records` : 'Not uploaded',
                        status: loaded ? 'In scope' : 'Missing',
                        action: loaded ? 'Included in audit export scope' : 'Upload to include in the audit pack',
                        tone: loaded ? 'ok' : 'medium',
                    };
                }),
            };
        },
    },
};

function OperationalInsightDetailView({ role, view }) {
    const navigate = useNavigate();
    const userRole = useContextRole();
    const data = useTodayWorkspaceData({ withActions: role === 'head' });

    if (data.loading) return <LoadingPane />;

    const builders = insightConfigBuilders[role] || {};
    const builder = builders[view] || builders[Object.keys(builders)[0]];
    if (!builder) return null;

    const config = builder(data);
    const hasRows = config.rows && config.rows.length > 0;

    return (
        <VStack spacing={5} align="stretch">
            <WorkspaceHero
                eyebrow={config.eyebrow}
                title={config.title}
                accent={config.accent}
                description={config.description}
                primaryAction={{ label: config.primary[0], icon: FiFlag, onClick: () => navigate(config.primary[1]) }}
                secondaryAction={{ label: config.secondary[0], icon: FiFileText, onClick: () => navigate(config.secondary[1]) }}
                kpis={config.kpis}
            />

            {hasRows ? (
                <WorkspaceCard p={0}>
                    <Flex justify="space-between" align="center" px={5} py={4} borderBottomWidth="1px" borderColor="brand.divider" gap={4}>
                        <Box>
                            <Heading size="sm">Contextual action queue</Heading>
                            <Text mt={1} fontSize="12px" color="brand.subtle">Every row is computed from this tenant's data and links to the next operational move.</Text>
                        </Box>
                        <ExportButton onClick={() => navigate('/reportsExports')} />
                    </Flex>
                    <Box overflowX="auto">
                        <Table size="sm">
                            <Thead bg="brand.cream">
                                <Tr>
                                    <Th>Signal</Th>
                                    <Th>KPI / metric</Th>
                                    <Th>Status</Th>
                                    <Th>Next action</Th>
                                    <Th />
                                </Tr>
                            </Thead>
                            <Tbody>
                                {config.rows.map((row) => (
                                    <Tr key={`${row.name}-${row.metric}`} bg={row.tone === 'high' ? 'red.50' : row.tone === 'medium' ? 'orange.50' : 'white'}>
                                        <Td fontWeight="800">{row.name}</Td>
                                        <Td color="brand.muted">{row.metric}</Td>
                                        <Td><StatusBadge tone={row.tone}>{row.status}</StatusBadge></Td>
                                        <Td color="brand.muted">{row.action}</Td>
                                        <Td textAlign="right">
                                            <Button size="sm" variant="outline" onClick={() => navigate(config.primary[1])}>Open</Button>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </WorkspaceCard>
            ) : (
                <SimpleGrid columns={{ base: 1, xl: config.empty?.showUploads ? 2 : 1 }} spacing={5}>
                    <WorkspaceCard>
                        <EmptyStateCard
                            icon={FiDatabase}
                            title={config.empty?.title || 'No live data for this view yet'}
                            description={config.empty?.description || 'This view is computed from tenant datasets and shows nothing until they are loaded.'}
                            hint={config.empty?.hint}
                            bg="transparent"
                            borderColor="transparent"
                            titleColor="gray.700"
                            descriptionColor="gray.600"
                            hintColor="gray.500"
                            iconColor="brand.teal"
                            maxW="full"
                        />
                    </WorkspaceCard>
                    {config.empty?.showUploads ? (
                        <NextUploadsCard
                            nextBestUploads={data.nextBestUploads}
                            isAdmin={userRole === 'admin'}
                            navigate={navigate}
                        />
                    ) : null}
                </SimpleGrid>
            )}
        </VStack>
    );
}

export const HeadInsightDetailView = ({ view }) => <OperationalInsightDetailView role="head" view={view} />;
export const PortfolioInsightDetailView = ({ view }) => <OperationalInsightDetailView role="portfolio" view={view} />;
export const ComplianceInsightDetailView = ({ view }) => <OperationalInsightDetailView role="compliance" view={view} />;

export function ViewOnlyTodayView() {
    const data = useTodayWorkspaceData({ withSignals: false });
    const { loading, counts, coverage } = data;

    if (loading) return <LoadingPane />;

    const loadedDatasets = FIRM_DATASET_TYPES.filter(([key]) => (counts[key] || 0) > 0).length;

    return (
        <VStack spacing={5} align="stretch">
            <WorkspaceHero
                eyebrow="Today · View only"
                title="Read-only stakeholder view."
                accent="No operational controls are exposed."
                description="This role gives controlled external visibility into live tenant coverage. All values are computed from the current datasets — nothing is simulated."
                kpis={[
                    { eyebrow: 'Scored coverage', value: formatPct(coverage.scored_customer_pct), sub: 'of customer base', icon: FiActivity },
                    { eyebrow: 'Client base', value: counts.customers ?? '—', sub: 'in tenant', icon: FiUsers },
                    { eyebrow: 'Evidence coverage', value: formatPct(coverage.customers_with_evidence_pct), sub: 'customers covered', icon: FiCheckCircle },
                    { eyebrow: 'Firm datasets', value: `${loadedDatasets}/${FIRM_DATASET_TYPES.length}`, sub: 'loaded', icon: FiDatabase },
                ]}
            />
        </VStack>
    );
}
